import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {PhotoLocation} from "../types/protocols";
import {useFirebaseImage} from "../Helpers/ImageLoader";
import LocationPin from "./LocationPin";
import PhotoOverlay from "./PhotoOverlay";
import {useZoomPan} from "../hooks/useZoomPan";

interface PhotoWithMarkerProps {
    photoLocation: PhotoLocation;
    title?: string;
    /** Omit the title heading entirely (the caller renders its own header). */
    hideTitle?: boolean;
    onOverlayOpen?: () => void;
    onOverlayClose?: () => void;
}

const PhotoWithMarker: React.FC<PhotoWithMarkerProps> = ({
    photoLocation,
    title = "Position i foto",
    hideTitle = false,
    onOverlayOpen,
    onOverlayClose
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const transformRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const { src, isLoading, hasError } = useFirebaseImage(photoLocation?.storagePath);

    // Full-screen overlay state
    const [showOverlay, setShowOverlay] = useState(false);

    // Calculate dynamic maximum zoom based on photo resolution
    const maxZoom = useMemo(() => {
        if (!photoLocation?.width || !photoLocation?.height || !containerWidth) {
            return 5;
        }
        const scaleDownRatio = containerWidth / photoLocation.width;
        return Math.min(Math.max(1 / scaleDownRatio, 2), 10);
    }, [photoLocation?.width, photoLocation?.height, containerWidth]);

    const handleClick = useCallback(() => {
        if (src) {
            setShowOverlay(true);
            if (onOverlayOpen) onOverlayOpen();
        }
    }, [src, onOverlayOpen]);

    const {zoom, pan, isDragging, isZoomed, handlers, handleZoom, resetZoom} = useZoomPan({
        maxZoom,
        wheelZoomFactor: 1.1,
        doubleClickZoomFactor: 1.5,
        wheelElementRef: containerRef,
        focalPointRef: containerRef,
        transformRef,
        ignoreSelector: '.marker',
        onClick: handleClick,
        enabled: !!src,
    });

    // Close overlay handler
    const handleCloseOverlay = useCallback(() => {
        setShowOverlay(false);
        if (onOverlayClose) onOverlayClose();
    }, [onOverlayClose]);

    // Marker position for inline view
    const markerPosition = useMemo(() => {
        if (!photoLocation || !containerWidth) return null;
        const scale = containerWidth / photoLocation.width;
        return {
            left: `${photoLocation.x * scale}px`,
            top: `${photoLocation.y * scale}px`,
        };
    }, [photoLocation, containerWidth]);

    // Update container width via ResizeObserver
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        const resizeObserver = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        updateWidth();

        return () => resizeObserver.disconnect();
    }, []);

    if (isLoading) {
        return <div>Laddar foto...</div>;
    }

    if (hasError || !src) {
        return <div>Foto saknas</div>;
    }

    return (
        <div>
            {!hideTitle && <h4 style={{marginBottom: '20px'}}>{title}</h4>}

            <div
                ref={containerRef}
                className="photo-container"
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '900px',
                    margin: 0,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    cursor: isDragging ? 'grabbing' : 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    userSelect: 'none',
                }}
                {...handlers}
            >
                {/* Zoom Controls */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    display: 'flex',
                    gap: '5px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    padding: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                    <ZoomButton
                        label="+"
                        title="Zooma in"
                        disabled={zoom >= maxZoom}
                        onClick={() => handleZoom('in')}
                    />
                    <ZoomButton
                        label="−"
                        title="Zooma ut"
                        disabled={zoom <= 0.5}
                        onClick={() => handleZoom('out')}
                    />
                    <ZoomButton
                        label="⟲"
                        title="Återställ"
                        disabled={!isZoomed}
                        onClick={resetZoom}
                        fontSize="16px"
                    />
                </div>

                <div
                    ref={transformRef}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '0 0',
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        position: 'relative',
                        width: '100%',
                        height: 'auto'
                    }}
                >
                    <img
                        src={src}
                        alt="Photo with marker"
                        className="photo-image"
                        onLoad={() => {
                            if (containerRef.current) {
                                setContainerWidth(containerRef.current.offsetWidth);
                            }
                        }}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            pointerEvents: 'none'
                        }}
                    />

                    {markerPosition && (
                        <LocationPin
                            left={markerPosition.left}
                            top={markerPosition.top}
                            size={44}
                            scale={1 / zoom}
                        />
                    )}
                </div>
            </div>

            {showOverlay && (
                <PhotoOverlay
                    src={src}
                    photoLocation={photoLocation}
                    onClose={handleCloseOverlay}
                />
            )}
        </div>
    );
};

/** Small zoom control button used in the inline photo viewer. */
const ZoomButton: React.FC<{
    label: string;
    title: string;
    disabled: boolean;
    onClick: () => void;
    fontSize?: string;
}> = ({label, title, disabled, onClick, fontSize = '18px'}) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            width: '32px',
            height: '32px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize,
            fontWeight: 'bold',
            color: disabled ? '#ccc' : '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
        }}
        title={title}
    >
        {label}
    </button>
);

export default PhotoWithMarker;
